import { useState, useEffect } from 'react';
import { companyService } from 'services/api';
import CompanyModal from './CompanyModal';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import Paginacion from 'components/Paginacion';
import styles from './Companies.module.css';

const LIMIT = 15;

export default function Companies() {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [search,   setSearch]   = useState('');
  const [page,     setPage]     = useState(1);
  const irAPagina = (n) => { setPage(n); window.scrollTo({ top: 0 }); };
  const [total,    setTotal]    = useState(0);
  const [alert,    setAlert]    = useState(null);
  const [modal,    setModal]    = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting,   setDeleting]   = useState(false);
  const [delError,   setDelError]   = useState('');

  const load = (p = page, q = search) => {
    setLoading(true);
    companyService.getAll({ page: p, limit: LIMIT, ...(q.trim() ? { search: q } : {}) })
      .then(r => {
        setItems(r.data.data || []);
        setTotal(r.data.total || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  // Cargar al montar y cuando cambia la página
  useEffect(() => { load(page, search); }, [page]); // eslint-disable-line

  // Búsqueda con debounce — resetea a página 1
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1, search); }, 400);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const openNew  = () => { setEditing(null); setModal(true); };
  const openEdit = (c) => { setEditing(c);   setModal(true); };

  const handleDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    setDelError('');
    try {
      await companyService.delete(confirmDel.id);
      setAlert({ type: 'success', message: 'Compañía eliminada' });
      setConfirmDel(null);
      load(page, search);
    } catch (err) {
      setDelError(err.response?.data?.message || 'No se pudo eliminar. Inténtalo de nuevo.');
    } finally {
      setDeleting(false);
    }
  };

  const onSaved = (msg) => {
    setAlert({ type: 'success', message: msg });
    setModal(false);
    load(page, search);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>
            <i className={`fas fa-briefcase ${styles.iconoCabecera}`} />
            Compañías
          </h1>
          <p>Gestiona las compañías registradas en el sistema</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>
          <i className="fas fa-plus" /> Nueva compañía
        </button>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type} ${styles.aviso}`} onClick={() => setAlert(null)}>
          {alert.message}
        </div>
      )}

      <div className="card">
        {/* Buscador */}
        <div className={styles.barra}>
          <div className={styles.buscadorCaja}>
            <i className={`fas fa-search ${styles.iconoBuscar}`} />
            <input type="text" className={`form-control ${styles.buscador}`}
              placeholder="Buscar por nombre de compañía..."
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <span className={styles.conteo}>
            {total} compañía{total !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-briefcase" />
            <p>{search ? 'No se encontraron compañías con ese nombre' : 'No hay compañías registradas aún'}</p>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Compañía</th>
                    <th>NIT / RUT</th>
                    <th>Contacto</th>
                    <th>Teléfono</th>
                    <th>Dirección</th>
                    <th className={styles.colCentro}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(c => (
                    <tr key={c.id}>
                      <td>
                        <div className={styles.nombre}>{c.name}</div>
                        {c.url && (
                          <div className={styles.url}>
                            {/^https?:\/\//i.test(c.url)
                              ? <a href={c.url} target="_blank" rel="noopener noreferrer">{c.url}</a>
                              : c.url}
                          </div>
                        )}
                      </td>
                      <td className={styles.celda}>{c.nit || '—'}</td>
                      <td>
                        <div className={styles.celda}>{c.contact_name || c.contact || '—'}</div>
                        {c.email && (
                          <div className={styles.correo}>{c.email}</div>
                        )}
                      </td>
                      <td className={styles.celda}>{c.phone || '—'}</td>
                      <td className={styles.celda}>{c.address || '—'}</td>
                      <td>
                        <div className={`actions ${styles.accionesCentro}`}>
                          <button className="btn btn-sm btn-secondary btn-icon"
                            onClick={() => openEdit(c)} title="Editar" aria-label="Editar">
                            <i className="fas fa-pencil" />
                          </button>
                          <button className="btn btn-sm btn-danger btn-icon"
                            onClick={() => setConfirmDel(c)} title="Eliminar" aria-label="Eliminar">
                            <i className="fas fa-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </>
        )}
      </div>
      {!loading && <Paginacion pagina={page} total={total} porPagina={LIMIT} onCambiar={irAPagina} />}

      {modal && <CompanyModal editing={editing} onClose={() => setModal(false)} onSaved={onSaved} />}

      {confirmDel && (
        <ConfirmDeleteModal title="Eliminar compañía" onClose={() => { setConfirmDel(null); setDelError(''); }} onConfirm={handleDelete} deleting={deleting} error={delError}>
          <div className={styles.confirmaTitulo}>
            ¿Eliminar <span className={styles.confirmaNombre}>{confirmDel.name}</span>?
          </div>
        </ConfirmDeleteModal>
      )}
    </div>
  );
}